import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  ClientProjectItem,
  ClientRecurringBillingItem,
  ClientWorkOrderItem,
} from "../../types/clients-module-types";
import { sortDataBy } from "../../../SharedModule/utils/dataSort";
import { createChangeOrderReducer } from "../../../SharedModule/utils/changeOrderReducer";
import {
  getDateFromDateString,
  LAST_MONTHS_START,
} from "../../../SharedModule/utils/dateUtils";

type ClientWorkState = {
  isLoadingWorkInfo: boolean;
  isLoadedWorkInfo: boolean;
  infoSavedClientId: number | null;
  clientRecurringBillingsInfo: ClientRecurringBillingItem[] | null;
  clientWorkOrdersInfo: ClientWorkOrderItem[] | null;
  clientProjectsInfo: ClientProjectItem[] | null;
  searchTextRecurring: string;
  searchTextWorkOrder: string;
  searchTextProject: string;
  seeRecurringActives: boolean;
  seeProjectActives: boolean;
  startDateWorkOrder: any;
  endDateWorkOrder: any;
  orderedBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string";
  };
  orderedWorkOrdersBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "number";
  };
  orderedProjectsBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string";
  };
};

const initialState: ClientWorkState = {
  isLoadingWorkInfo: true,
  isLoadedWorkInfo: false,
  infoSavedClientId: null,
  clientRecurringBillingsInfo: null,
  clientWorkOrdersInfo: null,
  clientProjectsInfo: null,
  searchTextRecurring: "",
  searchTextWorkOrder: "",
  searchTextProject: "",
  seeRecurringActives: true,
  seeProjectActives: true,
  startDateWorkOrder: getDateFromDateString(
    LAST_MONTHS_START.toISO({ includeOffset: false })
  ),
  endDateWorkOrder: new Date(),
  orderedBy: {
    orderBy: "startDate",
    criteria: "asc",
    dataType: "string",
  },
  orderedWorkOrdersBy: {
    orderBy: "id",
    criteria: "asc",
    dataType: "number",
  },
  orderedProjectsBy: {
    orderBy: "dueDate",
    criteria: "asc",
    dataType: "string",
  },
};

export const clientWorkSlice = createSlice({
  name: "clientWorkInfo",
  initialState,
  reducers: {
    loadWorkInfoTab: (state, action) => {
      state.infoSavedClientId = action.payload.clientId;
      state.clientRecurringBillingsInfo =
        action.payload.data.recurringBillings.length > 0
          ? action.payload.data.recurringBillings
          : null;
      state.clientWorkOrdersInfo =
        action.payload.data.workOrders.length > 0
          ? action.payload.data.workOrders
          : null;
      state.clientProjectsInfo =
        action.payload.data.projects.length > 0
          ? action.payload.data.projects
          : null;
      state.isLoadingWorkInfo = false;
      state.isLoadedWorkInfo = true;
    },
    setIsLoadingWorkInfo: (state, action) => {
      state.isLoadingWorkInfo = action.payload;
    },
    setSearchTextRecurring: (state, action) => {
      state.searchTextRecurring = action.payload;
    },
    setSearchTextWorkOrder: (state, action) => {
      state.searchTextWorkOrder = action.payload;
    },
    setSearchTextProject: (state, action) => {
      state.searchTextProject = action.payload;
    },
    setSeeRecurringActives: (state, action) => {
      state.seeRecurringActives = action.payload;
    },
    setSeeProjectActives: (state, action) => {
      state.seeProjectActives = action.payload;
    },
    setStartDateWorkOrder: (state, action) => {
      state.startDateWorkOrder = action.payload;
    },
    setEndDateWorkOrder: (state, action) => {
      state.endDateWorkOrder = action.payload;
    },
    resetPageFilters: (state) => {
      state.searchTextRecurring = initialState.searchTextRecurring;
      state.searchTextWorkOrder = initialState.searchTextWorkOrder;
      state.searchTextProject = initialState.searchTextProject;
      state.seeRecurringActives = initialState.seeRecurringActives;
      state.seeProjectActives = initialState.seeProjectActives;
      state.startDateWorkOrder = initialState.startDateWorkOrder;
      state.endDateWorkOrder = initialState.endDateWorkOrder;
      state.orderedBy = initialState.orderedBy;
      state.orderedWorkOrdersBy = initialState.orderedWorkOrdersBy;
      state.orderedProjectsBy = initialState.orderedProjectsBy;
    },
    changeOrder: createChangeOrderReducer("orderedBy"),
    changeWorkOrdersOrder: createChangeOrderReducer("orderedWorkOrdersBy"),
    changeProjectsOrder: createChangeOrderReducer("orderedProjectsBy"),
  },
});

// Action creators are generated for each case reducer function
export const {
  loadWorkInfoTab,
  setIsLoadingWorkInfo,
  setSearchTextRecurring,
  setSearchTextWorkOrder,
  setSearchTextProject,
  setSeeRecurringActives,
  setSeeProjectActives,
  setStartDateWorkOrder,
  setEndDateWorkOrder,
  resetPageFilters,
  changeOrder,
  changeWorkOrdersOrder,
  changeProjectsOrder,
} = clientWorkSlice.actions;

export const IsLoadingWorkInfo = (state: any) => {
  return state.clientWorkInfo.isLoadingWorkInfo;
};

export const IsLoadedWorkInfo = (state: any) => {
  return state.clientWorkInfo.isLoadedWorkInfo;
};

export const SelectSearchTextRecurring = (state: any) => {
  return state.clientWorkInfo.searchTextRecurring;
};

export const SelectSearchTextWorkOrder = (state: any) => {
  return state.clientWorkInfo.searchTextWorkOrder;
};

export const SelectSearchTextProject = (state: any) => {
  return state.clientWorkInfo.searchTextProject;
};

export const IsSeeRecurringActives = (state: any) => {
  return state.clientWorkInfo.seeRecurringActives;
};

export const IsSeeProjectActives = (state: any) => {
  return state.clientWorkInfo.seeProjectActives;
};

export const SelectInfoSavedClientId = (state: any) => {
  return state.clientWorkInfo.infoSavedClientId;
};

export const SelectStartDateWorkOrder = (state: any) => {
  return state.clientWorkInfo.startDateWorkOrder;
};

export const SelectEndDateWorkOrder = (state: any) => {
  return state.clientWorkInfo.endDateWorkOrder;
};

export const SelectClientBillingsServices = ({
  clientWorkInfo,
}: {
  clientWorkInfo: ClientWorkState;
}) => {
  return {
    clientRecurringBillingsInfo: clientWorkInfo.clientRecurringBillingsInfo,
  };
};

export const SelectOrderCriteria = ({
  clientWorkInfo,
}: {
  clientWorkInfo: ClientWorkState;
}) => clientWorkInfo.orderedBy;

export const SelectOrderedBy = createSelector(
  [SelectClientBillingsServices, SelectOrderCriteria],
  (elem, orderCriteria) => {
    return {
      clientRecurringBillingsInfo:
        (elem.clientRecurringBillingsInfo &&
          sortDataBy(
            elem.clientRecurringBillingsInfo,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        [],
    };
  }
);

export const SelectClientWorkOrders = ({
  clientWorkInfo,
}: {
  clientWorkInfo: ClientWorkState;
}) => {
  return {
    clientWorkOrdersInfo: clientWorkInfo.clientWorkOrdersInfo,
  };
};

export const SelectWorkOrdersOrderCriteria = ({
  clientWorkInfo,
}: {
  clientWorkInfo: ClientWorkState;
}) => clientWorkInfo.orderedWorkOrdersBy;

export const SelectWorkOrdersOrderedBy = createSelector(
  [SelectClientWorkOrders, SelectWorkOrdersOrderCriteria],
  (elem, orderCriteria) => {
    return {
      clientWorkOrdersInfo:
        (elem.clientWorkOrdersInfo &&
          sortDataBy(
            elem.clientWorkOrdersInfo,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        [],
    };
  }
);

export const SelectClientProjects = ({
  clientWorkInfo,
}: {
  clientWorkInfo: ClientWorkState;
}) => {
  return {
    clientProjectsInfo: clientWorkInfo.clientProjectsInfo,
  };
};

export const SelectProjectsOrderCriteria = ({
  clientWorkInfo,
}: {
  clientWorkInfo: ClientWorkState;
}) => clientWorkInfo.orderedProjectsBy;

export const SelectProjectsOrderedBy = createSelector(
  [SelectClientProjects, SelectProjectsOrderCriteria],
  (elem, orderCriteria) => {
    return {
      clientProjectsInfo:
        (elem.clientProjectsInfo &&
          sortDataBy(
            elem.clientProjectsInfo,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        [],
    };
  }
);

export default clientWorkSlice.reducer;
